"use client";

import { useEffect, useState } from "react";
import TopBar from "@/components/TopBar";
import CoinIcon from "@/components/CoinIcon";

type Player = {
  id: string;
  name: string;
  coins: number;
  redeemed: number;
  earned: number;
};

export default function LeaderboardPage() {
  const [players, setPlayers] = useState<Player[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    async function load() {
      const res = await fetch("/api/players", { cache: "no-store" });
      const j = await res.json();
      if (alive) {
        setPlayers(j.players ?? []);
        setLoaded(true);
      }
    }
    load();
    const t = setInterval(load, 4000); // live refresh during the event
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  const byCoins = [...players]
    .sort((a, b) => b.coins - a.coins)
    .filter((p) => p.coins > 0)
    .slice(0, 10);
  const byRedeemed = [...players]
    .sort((a, b) => b.redeemed - a.redeemed)
    .filter((p) => p.redeemed > 0)
    .slice(0, 10);

  return (
    <main className="arena-bg relative flex-1 flex flex-col">
      <TopBar title="LEADERBOARD" />
      <div className="flex-1 px-5 sm:px-8 py-8 max-w-5xl w-full mx-auto">
        <div className="grid gap-6 md:grid-cols-2">
          <Board
            title="MOST COINS"
            accent="sun"
            metric="coins"
            rows={byCoins}
            empty={loaded ? "No coins yet." : "Loading…"}
          />
          <Board
            title="MOST REDEEMED"
            accent="blue"
            metric="redeemed"
            rows={byRedeemed}
            empty={loaded ? "Nothing redeemed yet." : "Loading…"}
          />
        </div>
        <p className="mt-8 text-center text-white/30 text-xs font-display tracking-[0.3em] animate-blink">
          ● LIVE — updates every few seconds
        </p>
      </div>
    </main>
  );
}

function Board({
  title,
  accent,
  metric,
  rows,
  empty,
}: {
  title: string;
  accent: "sun" | "blue";
  metric: "coins" | "redeemed";
  rows: Player[];
  empty: string;
}) {
  const glow = accent === "sun" ? "text-sun glow-sun" : "text-electric glow-text";
  return (
    <section className="panel p-5">
      <h2 className={`headline text-2xl mb-4 ${glow}`}>{title}</h2>
      {rows.length === 0 ? (
        <p className="text-white/40 py-6 text-center">{empty}</p>
      ) : (
        <ol className="space-y-2">
          {rows.map((p, i) => (
            <li
              key={p.id}
              className="flex items-center gap-3 rounded-lg px-3 py-2.5 bg-navy/50 border border-white/5"
            >
              <Rank i={i} />
              <span className="flex-1 font-display text-lg text-white truncate">
                {p.name}
              </span>
              <span
                className={`coin-chip text-xl ${
                  accent === "blue" ? "text-electric" : ""
                }`}
              >
                <CoinIcon size={20} /> {p[metric]}
              </span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function Rank({ i }: { i: number }) {
  const medals = ["🥇", "🥈", "🥉"];
  if (i < 3) {
    return <span className="text-xl w-7 text-center">{medals[i]}</span>;
  }
  return (
    <span className="w-7 text-center font-display text-white/40">{i + 1}</span>
  );
}
